import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useRouteMatch } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Modal, Steps, Form, notification, Radio } from 'antd';

import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import {
  CreateQuotationItemRequest,
  QuotationGetResponse,
} from '@/core/domain/stock/Quotations/Quotations.types';
import {
  useCreateQuotationItem,
  useQuotationItems,
} from '@/core/domain/stock/Quotations/Quotations.hooks';

import { useDefaultFormErrorNotification } from '@/hooks';

import { ModalProps } from '@/types/ModalProps';

import QuotationItemsSetUpStep from './QuotationItemsSetUpStep';
import ItemsListByRadioSelected from '../ItemsListByRadioSelected';
import { RequestPurchases } from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';

export enum RadioSelect {
  Parts = 'PARTS',
  Consumables = 'CONSUMABLES',
  OS = 'OS',
  StockBellow = 'STOCK_BELLOW',
}

function CreateQuotationItemModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<CreateQuotationItemRequest>();
  const { quotationId } = useRouteMatch<{ quotationId: string }>().params;

  const [radioSelected, setRadioSelected] = useState<RadioSelect>(
    RadioSelect.Parts,
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedStockItems, setSelectedStockItems] = useState<StockItem[]>([]);
  const [selectedRequestPurchases, setSelectedRequestPurchases] = useState<
    RequestPurchases[]
  >([]);

  const { data: quotationItems } = useQuotationItems(+quotationId);

  const createMutation = useCreateQuotationItem(+quotationId);

  const quotation = useQueryClient().getQueryData<QuotationGetResponse>([
    'quotations',
    +quotationId,
  ]);

  const handleNextClick = () => {
    setCurrentStep(1);
  };

  const handleBackClick = () => {
    setCurrentStep(0);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const obj: CreateQuotationItemRequest = {
        ...values,
        quotationId: +quotationId,
      };

      await createMutation.mutateAsync(obj);

      notification.success({
        message: intl.formatMessage({
          id: 'quotations.details.items.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'quotations.details.items.create.notification.successDescription',
        }),
      });

      setCurrentStep(0);

      handleOnCancel();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const handleOnCancel = () => {
    setCurrentStep(0);
    form.resetFields();
    setSelectedStockItems([]);
    setSelectedRequestPurchases([]);
    handleVisible();
  };

  useEffect(() => {
    setSelectedStockItems([]);
    setSelectedRequestPurchases([]);
  }, [radioSelected]);

  const steps = [
    <ItemsListByRadioSelected
      enabled={visible}
      type={radioSelected}
      companiesIds={quotation ? [quotation?.result?.company.id] : []}
      quotationItems={quotationItems?.result}
      selectedStockItems={selectedStockItems}
      setSelectedStockItems={setSelectedStockItems}
      setSelectedRequestPurchases={setSelectedRequestPurchases}
      selectedRequestPurchases={selectedRequestPurchases}
      key={0}
    />,
    <QuotationItemsSetUpStep
      key={1}
      form={form}
      stockItems={selectedStockItems}
      requestPurchases={selectedRequestPurchases}
    />,
  ];

  const modalFooter = (
    <SpaceForButton justifyContent="end">
      <Button block onClick={handleOnCancel}>
        <FormattedMessage id="commons.modal.button.cancel" />
      </Button>

      {currentStep === 0 && (
        <Button
          block
          type="primary"
          disabled={
            radioSelected === RadioSelect.Parts ||
            radioSelected === RadioSelect.Consumables ||
            radioSelected === RadioSelect.StockBellow
              ? !selectedStockItems.length
              : !selectedRequestPurchases.length
          }
          onClick={handleNextClick}
        >
          <FormattedMessage id="commons.modal.button.next" />
        </Button>
      )}

      {currentStep === 1 && (
        <>
          <Button block onClick={handleBackClick}>
            <FormattedMessage id="commons.modal.button.back" />
          </Button>

          <Button
            type="primary"
            block
            onClick={handleSubmit}
            loading={createMutation.isLoading}
          >
            <FormattedMessage id="commons.modal.button.save" />
          </Button>
        </>
      )}
    </SpaceForButton>
  );

  return (
    <Modal
      open={visible}
      onCancel={handleOnCancel}
      title={intl.formatMessage({ id: 'quotations.details.items.create' })}
      footer={modalFooter}
      width={currentStep === 0 ? '100%' : 900}
      destroyOnClose
    >
      <Steps
        current={currentStep}
        style={{ marginBottom: 24 }}
        labelPlacement="vertical"
        percent={currentStep === 0 ? 50 : 90}
      >
        <Steps.Step
          title={intl.formatMessage({
            id: 'quotations.details.items.create.filter',
          })}
          description={intl.formatMessage({
            id: 'quotations.details.items.create.filter.description',
          })}
        />

        <Steps.Step
          title={intl.formatMessage({
            id: 'quotations.details.items.create.setUp',
          })}
          description={intl.formatMessage({
            id: 'quotations.details.items.create.setUp.description',
          })}
        />
      </Steps>

      {currentStep === 0 && (
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          size="large"
          onChange={({ target }) => setRadioSelected(target.value)}
          defaultValue={radioSelected}
          style={{ marginBottom: 20 }}
        >
          <Radio value={RadioSelect.Parts}>Peças</Radio>
          <Radio value={RadioSelect.Consumables}>Insumos</Radio>
          <Radio value={RadioSelect.OS}>Peça O.S.</Radio>
          <Radio value={RadioSelect.StockBellow}>
            Peça estoque abaixo do mínimo
          </Radio>
        </Radio.Group>
      )}

      {steps[currentStep]}
    </Modal>
  );
}

export default CreateQuotationItemModal;
